import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Mail, Loader2, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { superAdminApi } from '../../lib/superAdminApi';
import { toast } from 'sonner';

export default function SuperAdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('super_admin_token')) {
      superAdminApi.me().then(() => nav('/super-admin/dashboard')).catch(() => {
        localStorage.removeItem('super_admin_token');
      });
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await superAdminApi.login(email, password);
      localStorage.setItem('super_admin_token', data.token);
      localStorage.setItem('super_admin', JSON.stringify(data.super_admin));
      toast.success(`Bem-vindo, ${data.super_admin.name}`);
      nav('/super-admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-slate-100 flex items-center justify-center px-4" data-testid="super-admin-login-page">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-red-500 shadow-lg shadow-amber-500/30 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="super-admin-title">Super Admin</h1>
          <p className="text-slate-400 text-sm mt-2">Consola cross-tenant · NIS2AI Hub</p>
        </div>

        <form onSubmit={submit} className="space-y-5 bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <div className="space-y-2">
            <Label htmlFor="sa-email" className="text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email
            </Label>
            <Input
              id="sa-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="superadmin@nis2ai.pt"
              className="bg-slate-950/60 border-slate-700 text-slate-100 placeholder:text-slate-600"
              required
              data-testid="super-admin-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sa-pass" className="text-slate-300 flex items-center gap-2">
              <Lock className="w-4 h-4" /> Palavra-passe
            </Label>
            <Input
              id="sa-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="bg-slate-950/60 border-slate-700 text-slate-100"
              required
              data-testid="super-admin-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white font-semibold py-6"
            data-testid="super-admin-submit-button"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><KeyRound className="w-5 h-5 mr-2" /> Entrar como Super Admin</>)}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            Sessão válida 4 horas · Todas as acções são auditadas.
          </p>
        </form>
      </div>
    </div>
  );
}
